export const TELEGRAM_SERVICE_WORKER_MIGRATION_PARAM = 'swCleanup';

interface TelegramLaunchSignals {
  initData?: string;
}

export const isTelegramLaunch = ({
  initData,
}: TelegramLaunchSignals): boolean => Boolean(initData?.trim());

interface LegacyServiceWorker {
  scriptURL?: string;
}

interface LegacyServiceWorkerRegistration {
  active?: LegacyServiceWorker | null;
  installing?: LegacyServiceWorker | null;
  scope?: string;
  waiting?: LegacyServiceWorker | null;
  unregister: () => Promise<boolean>;
}

interface LegacyServiceWorkerContainer {
  controller?: LegacyServiceWorker | null;
  getRegistrations: () => Promise<
    readonly LegacyServiceWorkerRegistration[]
  >;
}

interface LegacyCacheStorage {
  keys: () => Promise<string[]>;
  delete: (key: string) => Promise<boolean>;
}

interface ReloadLocation {
  href: string;
  replace: (url: string) => void;
}

export interface TelegramServiceWorkerMigrationEnvironment {
  serviceWorker?: LegacyServiceWorkerContainer;
  cacheStorage?: LegacyCacheStorage;
  location?: ReloadLocation;
}

export interface TelegramServiceWorkerMigrationResult {
  registrationsRemoved: number;
  cachesRemoved: number;
  reloadScheduled: boolean;
  blockedByLegacyController: boolean;
}

const getBrowserMigrationEnvironment =
  (): TelegramServiceWorkerMigrationEnvironment => ({
    serviceWorker:
      typeof navigator === 'undefined'
        ? undefined
        : navigator.serviceWorker,
    cacheStorage:
      typeof caches === 'undefined'
        ? undefined
        : (caches as LegacyCacheStorage),
    location:
      typeof window === 'undefined'
        ? undefined
        : (window.location as ReloadLocation),
  });

const isLegacyOracleWorker = (worker?: LegacyServiceWorker | null) => {
  if (!worker?.scriptURL) return false;
  try {
    return new URL(worker.scriptURL).pathname === '/service-worker.js';
  } catch {
    return false;
  }
};

const isLegacyOracleRegistration = (
  registration: LegacyServiceWorkerRegistration,
  oracleRootScope?: string,
) =>
  Boolean(
    oracleRootScope &&
      registration.scope === oracleRootScope &&
      [registration.active, registration.waiting, registration.installing].some(
        isLegacyOracleWorker,
      ),
  );

const getOracleRootScope = (location?: ReloadLocation) => {
  if (!location) return undefined;
  try {
    return `${new URL(location.href).origin}/`;
  } catch {
    return undefined;
  }
};

const isLegacyOracleCache = (
  cacheName: string,
  oracleRootScope?: string,
) =>
  Boolean(
    oracleRootScope &&
      [
        `workbox-precache-v2-${oracleRootScope}`,
        `workbox-runtime-${oracleRootScope}`,
      ].includes(cacheName),
  );

/**
 * Ранние Mini App релизы регистрировали корневой Workbox. До импорта
 * приложения снимаем только этот worker и его Workbox-кэши. URL-маркер делает
 * обязательную перезагрузку идемпотентной даже в WKWebView без Web Storage.
 */
export const migrateLegacyTelegramServiceWorker = async (
  environment = getBrowserMigrationEnvironment(),
): Promise<TelegramServiceWorkerMigrationResult> => {
  const registrations = environment.serviceWorker
    ? await environment.serviceWorker.getRegistrations()
    : [];
  const oracleRootScope = getOracleRootScope(environment.location);
  const oracleRegistrations = registrations.filter(registration =>
    isLegacyOracleRegistration(registration, oracleRootScope),
  );
  const cacheKeys = environment.cacheStorage
    ? (await environment.cacheStorage.keys()).filter(cacheName =>
        isLegacyOracleCache(cacheName, oracleRootScope),
      )
    : [];

  const registrationResults = await Promise.all(
    oracleRegistrations.map(registration => registration.unregister()),
  );
  const cacheResults = environment.cacheStorage
    ? await Promise.all(
        cacheKeys.map(cacheKey => environment.cacheStorage!.delete(cacheKey)),
      )
    : [];

  const controllerWasActive = isLegacyOracleWorker(
    environment.serviceWorker?.controller,
  );
  const currentUrl = environment.location
    ? new URL(environment.location.href)
    : undefined;
  const alreadyReloaded =
    currentUrl?.searchParams.get(TELEGRAM_SERVICE_WORKER_MIGRATION_PARAM) ===
    '1';
  const reloadScheduled = Boolean(
    controllerWasActive && !alreadyReloaded && currentUrl && environment.location,
  );

  if (reloadScheduled && currentUrl && environment.location) {
    currentUrl.searchParams.set(TELEGRAM_SERVICE_WORKER_MIGRATION_PARAM, '1');
    environment.location.replace(currentUrl.toString());
  }

  return {
    registrationsRemoved: registrationResults.filter(Boolean).length,
    cachesRemoved: cacheResults.filter(Boolean).length,
    reloadScheduled,
    blockedByLegacyController: controllerWasActive && alreadyReloaded,
  };
};
